import React, {useState} from 'react';
import {BookmarkAdd} from '@mui/icons-material';
import {CircularProgress} from '@mui/material';
import {toast} from 'react-toastify';
import {trackProduct} from '../../product/infrastructure/productApi';
import {isSuccess} from '../../common/domain/RemoteData.model';
import {useCartContext} from '../context/CartContext';
import {useComercialContext} from '../../comercial/ComercialContext';

/**
 * Botón para resolver una línea del carrito sin stock suficiente:
 * - Si queda algo de stock, deja en el carrito las unidades disponibles y
 *   guarda como pendiente solo el exceso (lo que falta por servir).
 * - Si no queda stock, guarda como pendiente la cantidad completa y quita
 *   la línea del carrito.
 *
 * @param {{product: {id: number, code: string, quantity: number, stock: number}, onDone?: () => void}} props
 */
export const AddToPendingButton = ({product, onDone}) => {
  const [loading, setLoading] = useState(false);
  const {removeProduct, updateProductQuantity} = useCartContext();
  const {actingAsCliente} = useComercialContext();

  const available = Math.max(product.stock, 0);
  const pendingQuantity = product.quantity - available;

  const handleClick = async () => {
    setLoading(true);
    try {
      const result = await trackProduct(product.code, pendingQuantity, actingAsCliente?.numCliente);
      if (!isSuccess(result)) {
        toast.error('Error al añadir el producto a tus productos pendientes');
        return;
      }
      if (available > 0) {
        await updateProductQuantity(product, available);
        toast.success(`${available} ud(s). se quedan en tu carrito y ${pendingQuantity} en pendientes. Te avisaremos cuando haya stock!`);
      } else {
        await removeProduct(product);
        toast.success('Producto movido a pendientes. Te avisaremos cuando haya stock!');
      }
      onDone?.();
    } catch (error) {
      console.error('Failed to move product to pending:', error);
      toast.error('Error al añadir el producto a tus productos pendientes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button type="button"
      className="btn btn-warning d-flex align-items-center gap-1"
      onClick={handleClick}
      disabled={loading}>
      {loading ? <CircularProgress size={20}/> : <BookmarkAdd/>}
      {available > 0 ? `Añadir ${pendingQuantity} a pendientes` : 'Añadir a pendientes'}
    </button>
  );
};
